import { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { Add, ContentCopy, Edit, PauseCircle, PlayCircle, Refresh } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../services/api";

// Platform console (platform operators only): organisations, their plans and
// dedicated executors. Operators never see an organisation's tests, runs or
// users; the usage figures are counts.

const slugify = (name) =>
	name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 30);

// "" = no limit (null for the backend)
const limitOrNull = (v) => (v === "" || v == null ? null : Number(v));
const shown = (v) => (v == null ? "No limit" : v);
const errorMessage = (err, fallback) => err.response?.data?.message || fallback;

const EMPTY_TENANT = {
	name: "",
	slug: "",
	adminEmail: "",
	adminFirstName: "",
	adminLastName: "",
	maxParallelExecutions: "2",
	maxUsers: "5",
};

export default function PlatformTenants() {
	const { enqueueSnackbar } = useSnackbar();
	const [tenants, setTenants] = useState(null);
	const [executors, setExecutors] = useState([]);
	const [createOpen, setCreateOpen] = useState(false);
	const [form, setForm] = useState(EMPTY_TENANT);
	const [slugEdited, setSlugEdited] = useState(false);
	const [planTenant, setPlanTenant] = useState(null);
	const [plan, setPlan] = useState({ maxParallelExecutions: "", maxUsers: "" });
	const [executorOpen, setExecutorOpen] = useState(false);
	const [executorForm, setExecutorForm] = useState({ name: "", url: "", socketUrl: "" });
	const [newToken, setNewToken] = useState(null);
	const [busy, setBusy] = useState(false);

	const load = useCallback(() => {
		Promise.all([api.get("/platform/tenants"), api.get("/platform/executors")])
			.then(([t, e]) => {
				setTenants(t.data);
				setExecutors(e.data);
			})
			.catch((err) => enqueueSnackbar(errorMessage(err, "Failed to load organisations"), { variant: "error" }));
	}, [enqueueSnackbar]);

	useEffect(() => {
		load();
	}, [load]);

	const run = async (work, success) => {
		setBusy(true);
		try {
			await work();
			if (success) enqueueSnackbar(success, { variant: "success" });
			load();
			return true;
		} catch (err) {
			enqueueSnackbar(errorMessage(err, "Something went wrong"), { variant: "error" });
			return false;
		} finally {
			setBusy(false);
		}
	};

	const handleCreate = async () => {
		const ok = await run(
			() =>
				api.post("/platform/tenants", {
					name: form.name,
					slug: form.slug,
					adminEmail: form.adminEmail,
					adminFirstName: form.adminFirstName,
					adminLastName: form.adminLastName,
					plan: {
						maxParallelExecutions: limitOrNull(form.maxParallelExecutions),
						maxUsers: limitOrNull(form.maxUsers),
					},
				}),
			`Organisation created. ${form.adminEmail} was emailed a link to set their password.`,
		);
		if (ok) setCreateOpen(false);
	};

	const openPlan = (t) => {
		setPlanTenant(t);
		setPlan({
			maxParallelExecutions: t.plan?.maxParallelExecutions ?? "",
			maxUsers: t.plan?.maxUsers ?? "",
		});
	};

	const handlePlan = async () => {
		const ok = await run(
			() =>
				api.put(`/platform/tenants/${planTenant.id}/plan`, {
					maxParallelExecutions: limitOrNull(plan.maxParallelExecutions),
					maxUsers: limitOrNull(plan.maxUsers),
				}),
			"Plan updated",
		);
		if (ok) setPlanTenant(null);
	};

	const toggleStatus = (t) =>
		run(
			() => api.post(`/platform/tenants/${t.id}/${t.status === "ACTIVE" ? "suspend" : "resume"}`),
			t.status === "ACTIVE" ? `${t.name} suspended` : `${t.name} resumed`,
		);

	const handleRegisterExecutor = async () => {
		setBusy(true);
		try {
			const res = await api.post("/platform/executors", executorForm);
			setNewToken(res.data.token);
			setExecutorOpen(false);
			load();
		} catch (err) {
			enqueueSnackbar(errorMessage(err, "Failed to register the executor"), { variant: "error" });
		} finally {
			setBusy(false);
		}
	};

	const assignExecutor = (executorId, tenantId) =>
		run(
			() => api.put(`/platform/executors/${executorId}/tenant`, { tenantId: tenantId || null }),
			"Executor assignment saved",
		);

	const executorName = (id) => executors.find((e) => e.id === id)?.name;

	if (!tenants) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
				<Box>
					<Typography variant="h4" fontWeight={700}>
						Organisations
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Each organisation's data is kept in its own database. You manage plans and access, not their
						data.
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}>
					<IconButton onClick={load}>
						<Refresh />
					</IconButton>
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={() => {
							setForm(EMPTY_TENANT);
							setSlugEdited(false);
							setCreateOpen(true);
						}}
					>
						New organisation
					</Button>
				</Box>
			</Box>

			<Card sx={{ mb: 4 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Organisation</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Parallel slots</TableCell>
							<TableCell>Seats used</TableCell>
							<TableCell>Runs this month</TableCell>
							<TableCell>Running / queued</TableCell>
							<TableCell>Executor</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{tenants.map((t) => (
							<TableRow key={t.id}>
								<TableCell>
									<Typography variant="body2" fontWeight={600}>
										{t.name}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{t.id}
									</Typography>
								</TableCell>
								<TableCell>
									<Chip
										size="small"
										label={t.status === "ACTIVE" ? "Active" : "Suspended"}
										color={t.status === "ACTIVE" ? "success" : "warning"}
									/>
								</TableCell>
								<TableCell>{shown(t.plan?.maxParallelExecutions)}</TableCell>
								<TableCell>
									{t.usage?.activeUsers ?? "—"}
									{t.plan?.maxUsers != null && ` of ${t.plan.maxUsers}`}
								</TableCell>
								<TableCell>{t.usage?.runsThisMonth ?? "—"}</TableCell>
								<TableCell>
									{t.usage?.running ?? "—"} / {t.usage?.queued ?? "—"}
								</TableCell>
								<TableCell>{t.executorId ? executorName(t.executorId) || "Dedicated" : "Shared"}</TableCell>
								<TableCell align="right">
									<Tooltip title="Edit plan">
										<IconButton size="small" onClick={() => openPlan(t)}>
											<Edit fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title={t.status === "ACTIVE" ? "Suspend" : "Resume"}>
										<IconButton size="small" onClick={() => toggleStatus(t)} disabled={busy}>
											{t.status === "ACTIVE" ? (
												<PauseCircle fontSize="small" />
											) : (
												<PlayCircle fontSize="small" />
											)}
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Card>

			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Box>
					<Typography variant="h6" fontWeight={600}>
						Dedicated executors
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Organisations without one use the shared executor. A dedicated executor's token can only
						write its own organisation's runs.
					</Typography>
				</Box>
				<Button
					variant="outlined"
					startIcon={<Add />}
					onClick={() => {
						setExecutorForm({ name: "", url: "", socketUrl: "" });
						setExecutorOpen(true);
					}}
				>
					Register executor
				</Button>
			</Box>
			<Card>
				<CardContent>
					{executors.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							No dedicated executors.
						</Typography>
					) : (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>URL</TableCell>
									<TableCell>Live updates URL</TableCell>
									<TableCell>Organisation</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{executors.map((e) => (
									<TableRow key={e.id}>
										<TableCell>{e.name}</TableCell>
										<TableCell>{e.url}</TableCell>
										<TableCell>{e.socketUrl || "—"}</TableCell>
										<TableCell sx={{ minWidth: 200 }}>
											<TextField
												select
												size="small"
												fullWidth
												value={e.tenantId || ""}
												onChange={(ev) => assignExecutor(e.id, ev.target.value)}
												disabled={busy}
											>
												<MenuItem value="">Not assigned</MenuItem>
												{tenants.map((t) => (
													<MenuItem key={t.id} value={t.id}>
														{t.name}
													</MenuItem>
												))}
											</TextField>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* New organisation */}
			<Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>New organisation</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{ mt: 0 }}>
						<Grid item xs={12} sm={7}>
							<TextField
								label="Name"
								fullWidth
								value={form.name}
								onChange={(e) =>
									setForm({
										...form,
										name: e.target.value,
										slug: slugEdited ? form.slug : slugify(e.target.value),
									})
								}
							/>
						</Grid>
						<Grid item xs={12} sm={5}>
							<TextField
								label="Slug"
								fullWidth
								value={form.slug}
								onChange={(e) => {
									setSlugEdited(true);
									setForm({ ...form, slug: e.target.value });
								}}
								helperText="Permanent id: a-z, 0-9, hyphens"
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Admin email"
								type="email"
								fullWidth
								value={form.adminEmail}
								onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
								helperText="Any address (company or personal). It will belong to this organisation only; they get an email to set their password."
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								label="Admin first name"
								fullWidth
								value={form.adminFirstName}
								onChange={(e) => setForm({ ...form, adminFirstName: e.target.value })}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								label="Admin last name"
								fullWidth
								value={form.adminLastName}
								onChange={(e) => setForm({ ...form, adminLastName: e.target.value })}
							/>
						</Grid>
						<PlanFields
							value={{ maxParallelExecutions: form.maxParallelExecutions, maxUsers: form.maxUsers }}
							onChange={(p) => setForm({ ...form, ...p })}
						/>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleCreate}
						disabled={busy || !form.name || !form.slug || !form.adminEmail}
					>
						Create
					</Button>
				</DialogActions>
			</Dialog>

			{/* Edit plan */}
			<Dialog open={!!planTenant} onClose={() => setPlanTenant(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Plan: {planTenant?.name}</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{ mt: 0 }}>
						<PlanFields value={plan} onChange={(p) => setPlan({ ...plan, ...p })} />
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPlanTenant(null)}>Cancel</Button>
					<Button variant="contained" onClick={handlePlan} disabled={busy}>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			{/* Register executor */}
			<Dialog open={executorOpen} onClose={() => setExecutorOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Register a dedicated executor</DialogTitle>
				<DialogContent>
					<TextField
						label="Name"
						fullWidth
						sx={{ mt: 1, mb: 2 }}
						value={executorForm.name}
						onChange={(e) => setExecutorForm({ ...executorForm, name: e.target.value })}
					/>
					<TextField
						label="URL the backend calls"
						placeholder="http://acme-executor:3001"
						fullWidth
						sx={{ mb: 2 }}
						value={executorForm.url}
						onChange={(e) => setExecutorForm({ ...executorForm, url: e.target.value })}
					/>
					<TextField
						label="Live updates URL (optional)"
						placeholder="https://acme-executor.testify.saarkatech.com"
						fullWidth
						value={executorForm.socketUrl}
						onChange={(e) => setExecutorForm({ ...executorForm, socketUrl: e.target.value })}
						helperText="Where browsers connect for live steps: https, with the executor's CORS_ORIGINS set to the app's address."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setExecutorOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleRegisterExecutor}
						disabled={busy || !executorForm.name || !executorForm.url}
					>
						Register
					</Button>
				</DialogActions>
			</Dialog>

			{/* The token, once */}
			<Dialog open={!!newToken} onClose={() => setNewToken(null)} maxWidth="sm" fullWidth>
				<DialogTitle>Executor token</DialogTitle>
				<DialogContent>
					<Alert severity="warning" sx={{ mb: 2 }}>
						Set this as the executor's EXECUTOR_TOKEN now. It won't be shown again.
					</Alert>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<TextField value={newToken || ""} fullWidth size="small" InputProps={{ readOnly: true }} />
						<Tooltip title="Copy">
							<IconButton
								onClick={() =>
									navigator.clipboard
										?.writeText(newToken)
										.then(() => enqueueSnackbar("Copied", { variant: "success" }))
								}
							>
								<ContentCopy />
							</IconButton>
						</Tooltip>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button variant="contained" onClick={() => setNewToken(null)}>
						Done
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

// Parallel slots and seats; empty = no limit
function PlanFields({ value, onChange }) {
	return (
		<>
			<Grid item xs={6}>
				<TextField
					label="Parallel slots"
					type="number"
					fullWidth
					value={value.maxParallelExecutions}
					onChange={(e) => onChange({ maxParallelExecutions: e.target.value })}
					inputProps={{ min: 1, max: 50 }}
					helperText="1–50; empty = 50"
				/>
			</Grid>
			<Grid item xs={6}>
				<TextField
					label="Seats (active users)"
					type="number"
					fullWidth
					value={value.maxUsers}
					onChange={(e) => onChange({ maxUsers: e.target.value })}
					inputProps={{ min: 1 }}
					helperText="Empty = no limit"
				/>
			</Grid>
		</>
	);
}
